import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {chain} from 'lodash';
import {useMemo} from 'react';

import {useCrossProjectRunsProjectsQuery} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';

export type ProjectOption = {
  entityName: string;
  name: string;
  internalId: string;
};

export const useCrossProjectOptions = (
  queryString: string
): {
  loading: boolean;
  options: ProjectOption[];
  loadFailed: boolean;
} => {
  const viewer = useViewer();
  const {
    entityName: currentEntityName,
    projectName: currentProjectName,
    projectInternalId: currentProjectInternalId,
  } = useRunSelectorProjectInfo();
  const {data, loading, error} = useCrossProjectRunsProjectsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      entityName: currentEntityName,
      userName: viewer?.username ?? '',
      pattern: queryString,
    },
  });

  const options = useMemo(() => {
    const entityProjects = data?.entity?.projects?.edges.map(e => e.node) ?? [];
    const viewerProjects = data?.viewer?.projects?.edges.map(e => e.node) ?? [];
    const userProjects =
      data?.user?.teams?.edges.flatMap(edge =>
        edge.node?.projects?.edges.map(e => e.node)
      ) ?? [];

    return chain(userProjects)
      .concat(viewerProjects)
      .concat(entityProjects)
      .filter(isNotNullOrUndefined)
      .uniqBy(p => p.internalId)
      .orderBy(p => p.createdAt, 'desc')
      .map(p => ({
        entityName: p.entityName,
        name: p.name,
        internalId: p.internalId,
      }))
      .filter(p =>
        // Exclude current project
        currentProjectInternalId
          ? p.internalId !== currentProjectInternalId
          : !(
              p.entityName === currentEntityName &&
              p.name === currentProjectName
            )
      )
      .value();
  }, [data, currentEntityName, currentProjectName, currentProjectInternalId]);

  return {loading, options, loadFailed: error != null};
};
